import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import type { PaginatedResponse, Tenant, TenantStats } from "./types";

export function useTenants(params?: Record<string, string>) {
  return useQuery<PaginatedResponse<Tenant>>({
    queryKey: ["tenants", params],
    queryFn: async () => {
      const { data } = await client.get("/tenants/", { params });
      return data;
    },
  });
}

export function useTenantDetail(id: string) {
  return useQuery<Tenant>({
    queryKey: ["tenants", id],
    queryFn: async () => {
      const { data } = await client.get(`/tenants/${id}/`);
      return data;
    },
    enabled: !!id,
  });
}

export function useTenantStats() {
  return useQuery<TenantStats[]>({
    queryKey: ["tenants", "stats"],
    queryFn: async () => {
      const { data } = await client.get("/tenants/stats/");
      return data.data;
    },
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Tenant>) => {
      const { data } = await client.post("/tenants/", payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });
}

export function useUpdateTenant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Tenant>) => {
      const { data } = await client.patch(`/tenants/${id}/`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });
}

export function useDeleteTenant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await client.delete(`/tenants/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });
}
